import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { XMarkIcon, ExclamationTriangleIcon, CreditCardIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

interface ExpiringPaymentMethod {
  paymentMethodId: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  expirationDate: string;
  daysUntilExpiration: number;
  isExpired: boolean;
  isDefault: boolean;
}

interface PaymentExpirationNotificationProps {
  onClose?: () => void;
  showOnLogin?: boolean;
}

export default function PaymentExpirationNotification({ 
  onClose, 
  showOnLogin = false 
}: PaymentExpirationNotificationProps) {
  const { getAuthToken } = useAuth();
  const [expiringMethods, setExpiringMethods] = useState<ExpiringPaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const checkExpiringPaymentMethods = async () => {
      try {
        const authToken = getAuthToken();
        
        if (!authToken) {
          setLoading(false);
          return;
        }

        // REAL API CALL
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/check-expiring-payment-methods`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        // console.log("PaymentExpirationNotification: Response status:", response.status);

        if (!response.ok) {
          throw new Error("Failed to check expiring payment methods");
        }

        const data = await response.json();
        
        setExpiringMethods(data.expiringMethods || []);
        
        // Only show notification if there are expiring methods
        if (data.expiringMethods && data.expiringMethods.length > 0) {
          // console.log("PaymentExpirationNotification: Found expiring methods, checking if should show");
          
          // Check if user has dismissed the notification for today
          const remindLaterDate = localStorage.getItem('paymentExpirationRemindLater');
          if (remindLaterDate) {
            const remindDate = new Date(remindLaterDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Set to start of day
            
            if (remindDate > today) {
              // console.log("PaymentExpirationNotification: User dismissed for today, not showing");
              return;
            }
          }
          
          // console.log("PaymentExpirationNotification: Showing notification");
          setIsVisible(true);
        } else {
          // console.log("PaymentExpirationNotification: No expiring methods found");
        }
      } catch (err) {
        // console.error("PaymentExpirationNotification: Error:", err);
        setError(err instanceof Error ? err.message : "Failed to check payment methods");
      } finally {
        setLoading(false);
      }
    };

    checkExpiringPaymentMethods();
  }, [getAuthToken]);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  const handleRemindLater = () => {
    // Set a timestamp for 24 hours from now
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0); // Set to start of day
    
    localStorage.setItem('paymentExpirationRemindLater', tomorrow.toISOString());
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  const getUrgencyColor = (daysUntilExpiration: number) => {
    if (daysUntilExpiration <= 7) return "text-red-600 dark:text-red-400";
    if (daysUntilExpiration <= 14) return "text-orange-600 dark:text-orange-400";
    return "text-yellow-600 dark:text-yellow-400";
  };

  const getUrgencyBackground = (daysUntilExpiration: number) => {
    if (daysUntilExpiration <= 7) return "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800";
    if (daysUntilExpiration <= 14) return "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800";
    return "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800";
  };

  const getUrgencyIcon = (daysUntilExpiration: number) => {
    if (daysUntilExpiration <= 7) return "text-red-500";
    if (daysUntilExpiration <= 14) return "text-orange-500";
    return "text-yellow-500";
  };

  if (loading || !isVisible || expiringMethods.length === 0) {
    return null;
  }

  const urgentMethods = expiringMethods.filter(m => m.daysUntilExpiration <= 14);
  const warningMethods = expiringMethods.filter(m => m.daysUntilExpiration > 14);

  return (
    <div className="fixed inset-0 flex items-start justify-center px-4 py-6 pointer-events-none sm:p-6 z-[70]">
      <div className="flex w-full max-w-2xl flex-col items-center space-y-4">
        <div className="w-full bg-white dark:bg-gray-800 shadow-xl rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 transform transition-all ease-out duration-300">
          <div className="p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Your Payment Method is Expiring Soon!
                </h3>
                
                                 <div className="space-y-4">
                   {urgentMethods.length > 0 && (
                     <div className={`p-4 rounded-lg border ${getUrgencyBackground(urgentMethods[0].daysUntilExpiration)}`}>
                       <div className="flex items-center mb-2">
                         <ExclamationTriangleIcon className={`h-5 w-5 ${getUrgencyIcon(urgentMethods[0].daysUntilExpiration)} mr-2`} />
                         <span className={`font-medium ${getUrgencyColor(urgentMethods[0].daysUntilExpiration)}`}>
                           Urgent - Expiring within 14 days
                         </span>
                       </div>
                       <div className="space-y-2">
                         {urgentMethods.map((method) => (
                           <div key={method.paymentMethodId} className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded border">
                             <div className="flex items-center space-x-3">
                               <CreditCardIcon className="h-5 w-5 text-gray-400" />
                               <div>
                                 <p className="font-medium text-gray-900 dark:text-white">
                                   {method.brand} •••• {method.last4}
                                   {method.isDefault && <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">Default</span>}
                                 </p>
                                 <p className="text-sm text-gray-500 dark:text-gray-400">
                                   Expires {method.expMonth}/{method.expYear}
                                 </p>
                               </div>
                             </div>
                             <div className="text-right">
                               {/* Removed days left display */}
                             </div>
                           </div>
                         ))}
                       </div>
                     </div>
                   )}

                   {warningMethods.length > 0 && (
                     <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                       <div className="flex items-center mb-2">
                         <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mr-2" />
                         <span className="font-medium text-yellow-800 dark:text-yellow-200">
                           Warning - Expiring within 30 days
                         </span>
                       </div>
                       <div className="space-y-2">
                         {warningMethods.map((method) => (
                           <div key={method.paymentMethodId} className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded border">
                             <div className="flex items-center space-x-3">
                               <CreditCardIcon className="h-5 w-5 text-gray-400" />
                               <div>
                                 <p className="font-medium text-gray-900 dark:text-white">
                                   {method.brand} •••• {method.last4}
                                   {method.isDefault && <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">Default</span>}
                                 </p>
                                 <p className="text-sm text-gray-500 dark:text-gray-400">
                                   Expires {method.expMonth}/{method.expYear}
                                 </p>
                               </div>
                             </div>
                             <div className="text-right">
                               {/* Removed days left display */}
                             </div>
                           </div>
                         ))}
                       </div>
                     </div>
                   )}
                 </div>

                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/app/settings/billing"
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={() => {
                      // Clear the remind later setting when user takes action
                      localStorage.removeItem('paymentExpirationRemindLater');
                    }}
                  >
                    Update Payment Methods
                  </Link>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={handleRemindLater}
                  >
                    Remind me later
                  </button>
                </div>

                <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  <p>
                    To avoid service interruptions, please update your payment method before it expires.
                  </p>
                </div>
              </div>
              
              <div className="ml-4 flex-shrink-0 flex">
                <button
                  type="button"
                  className="rounded-full p-1 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                  onClick={() => {
                    // Clear the remind later setting when user manually closes
                    localStorage.removeItem('paymentExpirationRemindLater');
                    handleClose();
                  }}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-300" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
